/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Events} from '@exc/shared';
import type {RuntimeConfiguration} from '@adobe/exc-app';
import type {RuntimeMessenger} from './models/runtimeModels';

const PLATFORM_PATTERNS = [
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/dataset\/browse\/([a-zA-Z0-9-_]+)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/destination\/browse\/([a-zA-Z0-9-_]+)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/query\/edit\/([a-zA-Z0-9-_]*)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/schema\/browse\/https%3A%2F%2Fns\.adobe\.com%([a-zA-Z0-9-_]*)%2Fschemas%2F([a-zA-Z0-9-_]*)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/segment\/browse\/(.*)\/detail/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/source\/dataflows\/([a-zA-Z0-9-_]*)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/source\/accounts\/([a-zA-Z0-9-_]*)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/offers\/offer\/detail\/(personalized|fallback)\/([a-zA-Z0-9-_:]*)/,
  /https:\/\/([a-zA-Z0-9-_]*)\.adobe\.com(\/solutions\/experience-platform-platform-ui-client)*\/offers\/decision\/detail\/([a-zA-Z0-9-_:]*)/
];
const PLATFORM_ANALYTICS_PATTERNS = [
  /^https:\/\/(stage.|)analytics\.adobe\.(com|net)\/[a-zA-Z0-9-_?%.=$#&]*\/workspace\/edit\/[a-zA-Z0-9]*$/,
  /^https:\/\/(stage.|)analytics\.adobe\.(com|net)\/[a-zA-Z0-9-_?%.=$#&]*\/components\/calculatedMetrics\/edit\/[a-zA-Z0-9-_@]*$/
];
const ASSET_PATTERN = [
  // eslint-disable-next-line max-len
  /https:\/\/(exc-unifiedcontent\.)?experience(-stage|-qa)?\.adobe\.(net|com)(\/thunderbird|)\/solutions\/([a-zA-Z0-9-_?%.=&#]*)\/detail\/content\/dam\/(.*)/
];

const PATTERNS: Record<string, RegExp[]> = {
  ajoCampaignsApp: [
    /about:srcdoc#\/(delivery|summary)\/(?<id>[a-zA-Z0-9-_:]*)/
  ],
  'cjm-assets': ASSET_PATTERN,
  'cjm-journeys': [/https:\/\/journeys(-stage|-qa)?\.adobe\.com\/journey\/([a-zA-Z0-9-_]*)/],
  'cjm-message': [
    /https:\/\/exc-unifiedcontent\.experience(-stage|-qa)?\.adobe\.net\/solutions\/cjm-message-ui\/details\/([a-zA-Z0-9-_]*)/
  ],
  'cjm-platform': PLATFORM_PATTERNS,
  cjmFragmentsUI: [
    /https:\/\/([a-zA-Z0-9-_.]*)\.adobe\.(com|net)\/solutions\/cjm-fragments-ui.*#\/solutions\/cjm-fragments-ui\/details\/([a-zA-Z0-9-_:]*)/
  ],
  cjmLandingPagesUi: [
    /https:\/\/([a-zA-Z0-9-_.]*)\.adobe\.(com|net)\/solutions\/cjm-landing-pages-ui\/landing-pages\/details\/([a-zA-Z0-9-_:]*)/
  ],
  cjmSubscriptionsUi: [
    /https:\/\/exc-unifiedcontent\.experience(-stage|-qa)?\.adobe\.net\/solutions\/cjm-landing-pages-ui\/subscriptions\/edit\/([a-zA-Z0-9-_]*)/
  ],
  cjmTemplateFragmentUI: [
    /https:\/\/([a-zA-Z0-9-_.]*)\.adobe\.(com|net)\/solutions\/cjm-template-fragment-ui\/details\/([a-zA-Z0-9-_:]*)/
  ],
  ddam: ASSET_PATTERN,
  experiencePlatformUI: PLATFORM_PATTERNS,
  platformAnalytics: PLATFORM_ANALYTICS_PATTERNS
};

export interface RecentEventConfig {
  appId?: string;
  authId?: string;
  capability: null;
  clickTimestamp: number;
  href: string;
  orgId?: string;
  sandboxName?: string;
  sandboxType?: string;
  tenantId?: string;
  userId?: string;
}

export class Recents {
  private runtimeConfig: RuntimeConfiguration | undefined;
  private readonly messenger: RuntimeMessenger;
  private readonly location: Location;

  constructor(messenger: RuntimeMessenger, window: Window) {
    this.messenger = messenger;
    this.location = window.location;
  }

  createConfig(): RecentEventConfig {
    const {appId, imsOrg, imsProfile, sandbox, tenant} = this.runtimeConfig || {};
    return {
      appId,
      authId: imsProfile?.authId,
      capability: null,
      clickTimestamp: Date.now(),
      href: this.location.href,
      orgId: imsOrg,
      sandboxName: sandbox?.name,
      sandboxType: sandbox?.type,
      tenantId: tenant,
      userId: imsProfile?.userId
    };
  }

  /**
   * Checks if the provided recent href is valid to be stored as a recent. This
   * check currently happens in the backend when storing recents in settings, so
   * we're reducing the unnecessary stored recents data.
   * @param href - Window href for checking recents against.
   * @param appId - App ID for checking recents against.
   * @returns true if it's a valid recent, false otherwise.
   */
  isValid(href: string, appId = ''): boolean {
    return (PATTERNS[appId] || []).some((pattern: RegExp): boolean => pattern.test(href));
  }

  /**
   * Receives a RuntimeConfiguration object for later use.
   * @param config - RuntimeConfiguration object to set.
   */
  receiveConfig(config: RuntimeConfiguration): void {
    this.runtimeConfig = config;
  }

  /**
   * Track recents events, sending them to the Unified Shell via post message
   * using the `messenger` instance.
   * @async
   * @param event - Event or array of events to track.
   * @param args - Custom args to include in the request.
   */
  track(event: string | string[], args: any[]): Promise<void[]> {
    const config = this.createConfig();
    return Promise.all(
      (Array.isArray(event) ? event : [event]).map((eventName: string) =>
        this.messenger.send(Events.RECENT_EVENT, {args, ...config, event: eventName})
      )
    );
  }
}

export default (messenger: RuntimeMessenger, window: Window) => new Recents(messenger, window);
